import {
    capitalizeString,
    DateFormats,
    formatDate,
    formatNameFirstLast,
} from '@strata/helper-functions';
import { formatDollarAmount } from '@strata/react-dollar-amount';
import { InlineMessage } from '@strata/react-inline-message';
import { ClaimStatusIndicator, StatusIndicator } from '@strata/react-status-indicators';

import {
    formatClaimRejectCodes,
    formatCodeList,
    formatDrugDescription,
    formatRejectMessage,
    getClaimProductLabel,
} from 'services/claimInsightsHelpers';

const { Statuses } = ClaimStatusIndicator;

const TestClaimStatusByDisposition = {
    paid: Statuses.PAID,
    rejected: Statuses.REJECTED,
};

const TestClaimConsequenceByOutcome = {
    AWAITING_AUTH: {
        intent: InlineMessage.Intent.WARNING,
        label: (rejectCode) =>
            `Reject ${rejectCode} keeps this treatment manageable — the referral was moved to Awaiting Auth rather than unmanaged.`,
    },
    UNMANAGE: {
        intent: InlineMessage.Intent.WARNING,
        label: (rejectCode) =>
            `Reject ${rejectCode} is not manageable — the treatment was unmanaged.`,
    },
    ASSIGN: {
        intent: InlineMessage.Intent.INFO,
        label: (rejectCode) =>
            `Reject ${rejectCode} keeps this treatment manageable — the claim is still assignable.`,
    },
};

const composeNpiWithName = (name, npi) => (name ? `${name} (${npi})` : npi);

const formatYesNo = (value) => {
    if (value == null) {
        return null;
    }
    return value ? 'Yes' : 'No';
};

const formatReferral = (referral) => {
    if (!referral) {
        return null;
    }

    const referralLabel = referral.friendlyId ?? referral.referralId;
    return referral.referralStatus
        ? `${referralLabel} (${referral.referralStatus})`
        : referralLabel;
};

const section = (title, fields) => ({ title, fields });

const getDisplayRejectCode = ({ rejectCode, testRejectCodes }) =>
    rejectCode ?? testRejectCodes?.[0]?.code;

const getDisplayRejectMessage = ({ rejectCode, testRejectCodes, rejectMessages }) =>
    (testRejectCodes?.find(({ code }) => code === rejectCode) ?? testRejectCodes?.[0])?.message ??
    rejectMessages?.[0];

const formatPrimaryReject = (claim) => {
    const code = getDisplayRejectCode(claim);
    const message = getDisplayRejectMessage(claim);
    return (
        [code, message ? formatRejectMessage(message) : null].filter(Boolean).join(' · ') ||
        'None returned'
    );
};

const getTestClaimDispositionValue = (claim) => {
    if (claim.claimStatus === Statuses.PAID || claim.claimStatus === Statuses.REJECTED) {
        return <ClaimStatusIndicator status={claim.claimStatus} />;
    }

    return (
        <StatusIndicator
            status={StatusIndicator.Status.DEFAULT}
            message="No Response"
            tooltipText="The PBM returned no disposition — treat as not payable."
        />
    );
};

const getTestClaimDetailSections = (claim, names = {}) => {
    const isRejected = claim.disposition === 'rejected';
    const rejectCodesReturned = (claim.testRejectCodes ?? [])
        .map(({ code, message }) =>
            [code, message ? formatRejectMessage(message) : null].filter(Boolean).join(' · '),
        )
        .filter(Boolean)
        .join(', ');
    const rejectMessages = (claim.rejectMessages ?? [])
        .filter(Boolean)
        .map(formatRejectMessage)
        .join(' · ');

    return [
        section('Result', [
            {
                label: 'Disposition',
                value: getTestClaimDispositionValue(claim),
            },
            ...(isRejected ? [{ label: 'Reject Code', value: formatPrimaryReject(claim) }] : []),
            {
                label: 'Run At',
                value: formatDate(claim.adjudicationDate, DateFormats.DATE_TIME),
            },
        ]),
        section('Supplier', [
            {
                label: 'Billing Supplier',
                value: composeNpiWithName(names.billingSupplierName, claim.billingSupplierNpi),
            },
            {
                label: 'Prescriber',
                value: composeNpiWithName(names.prescriberName, claim.prescriberNpi),
            },
        ]),
        ...(rejectCodesReturned || rejectMessages
            ? [
                  section('Rejects', [
                      ...(rejectCodesReturned
                          ? [{ label: 'Reject Codes Returned', value: rejectCodesReturned }]
                          : []),
                      ...(rejectMessages
                          ? [{ label: 'Messages Without a Reject Code', value: rejectMessages }]
                          : []),
                  ]),
              ]
            : []),
        section('Request', [
            { label: 'NDC', value: claim.ndc },
            { label: 'Pharmacy NPI', value: claim.billingSupplierNpi },
            { label: 'Prescriber NPI', value: claim.prescriberNpi },
            { label: 'Date of Service', value: formatDate(claim.claimDateOfService) },
            { label: 'Quantity', value: claim.quantity },
            { label: 'Days Supply', value: claim.drugDaysSupply },
            { label: 'DAW', value: claim.dispenseAsWrittenCode },
            { label: 'Member ID', value: claim.memberId },
        ]),
    ];
};

const getPaidClaimDetailSections = (claim, names = {}) => [
    section('Patient', [
        {
            label: 'Patient Name',
            value: formatNameFirstLast(claim.patientFirstName, claim.patientLastName),
        },
        { label: 'DOB', value: formatDate(claim.patientDob) },
        { label: 'Member ID', value: claim.memberId },
        {
            label: 'Diagnosis Code(s)',
            value: formatCodeList(claim.diagnosisCodes) ?? claim.diagnosisCode,
        },
    ]),
    section('Prescription', [
        { label: 'Prescription #', value: claim.prescriptionNumber },
        {
            label: 'Prescriber',
            value: composeNpiWithName(names.prescriberName, claim.prescriberNpi),
        },
        { label: 'B/G Code', value: claim.drugBrandGenericCode },
        { label: 'Benefit', value: capitalizeString(claim.benefit) },
    ]),
    section('Billing', [
        { label: 'Ingredient Cost', value: formatDollarAmount(claim.drugIngredientCost) },
        { label: 'Submitted Cost', value: formatDollarAmount(claim.submittedCost) },
        {
            label: 'Usual and Customary',
            value: formatDollarAmount(claim.usualAndCustomary),
        },
        { label: 'Referent Price Type', value: claim.priceType },
        { label: 'Sub-clarification Code', value: claim.subClarificationCode },
        { label: 'DAW', value: claim.dispenseAsWrittenCode },
        { label: 'Overrides', value: formatCodeList(claim.claimOverrideCodes) },
    ]),
    section('Supplier', [
        {
            label: 'Assigned Supplier',
            value: composeNpiWithName(names.assignedSupplierName, claim.assignedSupplierNpi),
        },
        {
            label: 'Billing Supplier',
            value: composeNpiWithName(names.billingSupplierName, claim.billingSupplierNpi),
        },
    ]),
    section('Misc.', [
        { label: 'Contingent Therapy', value: formatYesNo(claim.isContingentTherapy) },
        ...(claim.claimStatus === Statuses.REJECTED
            ? [
                  { label: 'Reject Codes', value: formatClaimRejectCodes(claim) },
                  { label: 'Fill #', value: claim.drugFillNumber },
                  { label: 'Referral', value: formatReferral(claim.referral) },
              ]
            : []),
    ]),
];

export const getClaimDetailSections = (claim, names = {}) =>
    claim.isTestClaim
        ? getTestClaimDetailSections(claim, names)
        : getPaidClaimDetailSections(claim, names);

export const mapTestClaimForGrid = (testClaim) => ({
    isTestClaim: true,
    claimSource: 'FMH Test',
    claimId: testClaim.testClaimRequestId,
    claimDateOfService: testClaim.dateOfService,
    adjudicationDate: testClaim.createdAt,
    claimStatus: TestClaimStatusByDisposition[testClaim.disposition] ?? null,
    disposition: testClaim.disposition ?? null,
    ndc: testClaim.ndc,
    quantity: testClaim.quantity,
    drugDaysSupply: testClaim.daysSupply,
    dispenseAsWrittenCode: testClaim.dawCode,
    memberId: testClaim.memberId,
    prescriberNpi: testClaim.prescriberNpi,
    billingSupplierNpi: testClaim.pharmacyNpi,
    rejectCode: testClaim.rejectCode,
    outcome: testClaim.outcome,
    rejectCodes: (testClaim.rejectCodes ?? []).map(({ code }) => code).filter(Boolean),
    testRejectCodes: testClaim.rejectCodes ?? [],
    rejectMessages: testClaim.rejectMessages ?? [],
});

export const getTestClaimConsequence = (claim) => {
    const consequence = TestClaimConsequenceByOutcome[claim.outcome];
    if (!consequence) {
        return null;
    }

    const rejectCode = claim.rejectCode ?? claim.testRejectCodes?.[0]?.code;
    return {
        intent: consequence.intent,
        label: consequence.label(rejectCode),
    };
};

export const getPaidClaimDetailTitle = (claim) =>
    claim.claimDateOfService
        ? `${formatDate(claim.claimDateOfService)} · ${claim.claimId ?? 'Claim'}`
        : (claim.claimId ?? 'Claim detail');

export const getClaimDetailTitle = (claim) => {
    if (claim.isTestClaim) {
        const dateLabel = formatDate(claim.claimDateOfService);
        return dateLabel ? `${dateLabel} · FMH Test Claim` : 'FMH Test Claim';
    }

    return getPaidClaimDetailTitle(claim);
};

export const enrichClaimDrugNames = (claims, productServiceMap) =>
    (claims ?? []).map((claim) => {
        const drugName =
            formatDrugDescription(claim) ?? getClaimProductLabel(claim, productServiceMap);
        return drugName ? { ...claim, drugName } : claim;
    });

/** True when claim B/G conflicts with the treatment's brand/generic setting. */
export const hasBrandGenericMismatch = (claim, treatmentIsBrand) => {
    if (treatmentIsBrand == null || claim.isBrand == null) {
        return false;
    }
    return claim.isBrand !== treatmentIsBrand;
};

/** True when assigned and billing supplier NPIs both exist and differ. */
export const hasSupplierMismatch = (claim) =>
    Boolean(
        claim.assignedSupplierNpi &&
        claim.billingSupplierNpi &&
        claim.assignedSupplierNpi !== claim.billingSupplierNpi,
    );
